import { useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { MailCheck, Send } from 'lucide-react'
import { AuthShell } from './AuthShell'
import { Button, ErrorNote, Field, Input } from '@/components/ui'
import { useAuth } from '@/lib/auth'
import { authErrorMessage } from '@/lib/authErrors'

export function ForgotPasswordPage() {
  const { resetPassword } = useAuth()
  const [email, setEmail] = useState('')
  const [sent, setSent] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()
    setError(null)
    setBusy(true)
    try {
      await resetPassword(email)
      setSent(true)
    } catch (caught) {
      // Vi berättar aldrig om adressen finns eller inte – det skulle avslöja
      // vilka som är med i häftet. Bara riktiga fel visas.
      const message = authErrorMessage(caught)
      if (message.includes('Fel e-post')) setSent(true)
      else setError(message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <AuthShell
      title="Glömt lösenordet"
      intro={
        sent
          ? undefined
          : 'Fyll i din e-postadress så skickar vi en länk där du kan välja ett nytt lösenord.'
      }
      footer={
        <Link to="/logga-in" className="font-semibold text-accent underline underline-offset-2">
          Tillbaka till inloggningen
        </Link>
      }
    >
      {sent ? (
        <div className="flex flex-col items-center gap-3 py-4 text-center">
          <MailCheck size={40} className="text-success" aria-hidden />
          <h2 className="font-display text-lg font-extrabold">Kolla din mejl</h2>
          <p className="text-sm text-muted text-balance">
            Finns det ett konto på <strong className="text-ink">{email}</strong> har vi skickat en
            återställningslänk dit. Titta även i skräpposten.
          </p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          {error && <ErrorNote>{error}</ErrorNote>}

          <Field label="E-post" required>
            {({ id, describedBy }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                type="email"
                inputMode="email"
                autoComplete="email"
                autoCapitalize="none"
                required
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="namn@exempel.se"
              />
            )}
          </Field>

          <Button type="submit" size="lg" fullWidth loading={busy} icon={<Send size={18} />}>
            Skicka återställningslänk
          </Button>
        </form>
      )}
    </AuthShell>
  )
}
